import { getAppPrefix, isGatewayServed } from './apollo';

/**
 * Gateway-shell detection.
 *
 * When the Studio SPA is served *through* the separable gateway, auth is
 * gateway-owned (a single `_matrix_gateway` cookie for the origin), so sibling
 * apps (`/studio`, `/admin`, `/fleet`) iframed same-origin are already
 * authenticated. In that mode — and ONLY that mode — we render the
 * `<GatewayShell>` (a left rail + iframe pane) instead of the normal app.
 *
 * Direct access (dev :4000, Vite :5173, direct :443, production ingress) must
 * be visually and behaviorally identical to today, so every check below must
 * return false unless we are genuinely behind the gateway.
 *
 * The backend injects the flag in one of several forms; we support all of them
 * defensively so we're robust to whichever the backend ends up picking:
 *   1. `<meta name="gateway-shell" content="1">` in the served SPA HTML.
 *   2. `window.__GATEWAY_SHELL__ = true` inline script.
 *   3. The existing `getAppPrefix()` heuristic (non-empty only on the local
 *      gateway at :5000), which already gates apollo/socket routing.
 */

declare global {
  interface Window {
    __GATEWAY_SHELL__?: boolean;
  }
}

/**
 * Reads the `<meta name="gateway-shell" content="1">` tag the gateway injects
 * into the served SPA HTML. Any truthy content ("1", "true") counts.
 */
function hasGatewayShellMeta(): boolean {
  if (typeof document === 'undefined') return false;
  const content = document
    .querySelector('meta[name="gateway-shell"]')
    ?.getAttribute('content')
    ?.trim()
    .toLowerCase();
  return content === '1' || content === 'true';
}

/**
 * Reads the `window.__GATEWAY_SHELL__` global the gateway may inject via an
 * inline script.
 */
function hasGatewayShellGlobal(): boolean {
  if (typeof window === 'undefined') return false;
  return window.__GATEWAY_SHELL__ === true;
}

/**
 * True when any gateway-shell signal is present. Does NOT consider frame
 * nesting — use `shouldRenderGatewayShell()` for the render decision.
 *
 * `getAppPrefix()` is itself gated on `isGatewayServed()` (the meta/global
 * flag), so a non-gateway deployment that serves the SPA at `/studio` never
 * counts as shell mode — the path segment alone is not a gateway signal.
 * `isGatewayServed()` is included explicitly so the intent survives even if
 * the prefix heuristic changes.
 */
export function isGatewayShellMode(): boolean {
  return (
    hasGatewayShellMeta() || hasGatewayShellGlobal() || isGatewayServed() || getAppPrefix() !== ''
  );
}

/**
 * True only when this window is the top-level window (not inside an iframe).
 *
 * The shell renders `/studio` in its own iframe; the SPA loaded *inside* that
 * iframe must NOT render another shell (which would nest infinitely). Gating
 * on `window.top === window.self` guarantees the shell only ever mounts once,
 * at the very top. Cross-origin access to `window.top` can throw, so we treat
 * any such failure as "we are framed" (the safe, non-nesting default).
 */
export function isTopWindow(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.top === window.self;
  } catch {
    return false;
  }
}

/**
 * The single render decision: render the gateway shell only when we are behind
 * the gateway AND we are the top-level window. With the flag absent, or inside
 * a frame, this returns false and the app renders exactly as it does today.
 */
export function shouldRenderGatewayShell(): boolean {
  return isTopWindow() && isGatewayShellMode();
}

/**
 * The signed-in operator, surfaced to the launcher footer. The gateway stamps
 * `x-matrix-email` / `x-matrix-realm` / `x-matrix-roles` onto every proxied
 * request; `StaticHtmlHelper` mirrors them into `<meta>` tags (GW-810), which
 * we read here. `user` is the email's local part (or the raw id); `role` is a
 * friendly primary role.
 */
export type ShellIdentity = { user: string; realm: string; role: string };

function metaContent(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const c = document.querySelector(`meta[name="${name}"]`)?.getAttribute('content')?.trim();
  return c || null;
}

/** Collapse a role list to one short, human label (prefers an admin role). */
function primaryRole(roles: string[]): string {
  if (!roles.length) return '';
  if (roles.some((r) => /admin/i.test(r))) return 'admin';
  return roles[0];
}

/** Reads the operator identity from the gateway-injected metas, or null. */
export function readShellIdentity(): ShellIdentity | null {
  const rawUser = metaContent('gateway-user');
  const realm = metaContent('gateway-realm') ?? '';
  const rawRoles = metaContent('gateway-roles');
  if (!rawUser && !realm && !rawRoles) return null;

  let role = '';
  if (rawRoles) {
    try {
      const parsed = JSON.parse(rawRoles);
      role = Array.isArray(parsed) ? primaryRole(parsed as string[]) : String(parsed);
    } catch {
      role = rawRoles;
    }
  }

  return { user: rawUser ? rawUser.split('@')[0] : 'user', realm, role };
}
